import { computed } from 'vue'
import { useTodayCalculations } from './useTodayCalculations'
import { useSavingsGoals } from './useSavingsGoals'

export function useRemainingBudget() {
  const { todayAvailableBudget, todaySavingsContributions } = useTodayCalculations()
  const { totalDailyAllocation } = useSavingsGoals()

  // The available budget already accounts for actual savings contributions
  // This is the amount available to spend on regular expenses
  const adjustedRemainingBudget = computed(() => {
    const value = todayAvailableBudget.value
    console.log('💰 adjustedRemainingBudget recalculated:', value)
    // Ensure we never return negative zero
    return value === 0 ? 0 : value
  })

  // Calculate remaining savings target for today
  const remainingSavingsTarget = computed(() => {
    const recommended = totalDailyAllocation.value
    const alreadyContributed = todaySavingsContributions.value
    return Math.max(0, recommended - alreadyContributed)
  })

  // Amount available for spending after considering savings goals
  // This subtracts the remaining savings target from available budget
  const remainingAfterSavingsGoals = computed(() => {
    const value = adjustedRemainingBudget.value - remainingSavingsTarget.value
    // Ensure we never return negative zero
    return value === 0 ? 0 : value
  })

  // Savings guidance status
  const savingsGuidanceStatus = computed(() => {
    const available = adjustedRemainingBudget.value
    const recommended = totalDailyAllocation.value
    const alreadyContributed = todaySavingsContributions.value
    const remainingTarget = remainingSavingsTarget.value

    if (recommended === 0) return 'no-goals'
    if (alreadyContributed >= recommended) return 'target-met'
    if (available >= remainingTarget) return 'on-track'
    if (available > 0) return 'partial'
    return 'no-budget'
  })

  return {
    // Main budget values
    adjustedRemainingBudget,
    remainingSavingsTarget,
    remainingAfterSavingsGoals,

    // Savings values for template access
    totalDailyAllocation,

    // Savings guidance
    savingsGuidanceStatus,

    // Helper methods
    formatCurrency: (value) => {
      const num = Number(value);
      const rounded = Math.round(num * 100) / 100;
      return Object.is(rounded, -0) ? "0.00" : rounded.toFixed(2);
    }
    ,

    getSavingsProgress: () => {
      const recommended = totalDailyAllocation.value
      const alreadyContributed = todaySavingsContributions.value
      if (recommended === 0) return 0
      return Math.min(100, (alreadyContributed / recommended) * 100)
    },

    getSavingsStatusText: () => {
      const available = adjustedRemainingBudget.value
      const remainingTarget = remainingSavingsTarget.value

      switch (savingsGuidanceStatus.value) {
        case 'target-met': return 'Target Achieved!'
        case 'on-track': return '✓ Remaining Affordable'
        case 'partial': return `⚠ $${(remainingTarget - available).toFixed(2)} Short`
        case 'no-budget': return '✗ No Budget Available'
        default: return ''
      }
    },

    getSavingsGuidanceMessage: () => {
      const available = adjustedRemainingBudget.value
      const recommended = totalDailyAllocation.value
      const alreadyContributed = todaySavingsContributions.value
      const remainingTarget = remainingSavingsTarget.value

      switch (savingsGuidanceStatus.value) {
        case 'target-met':
          return `Great job! You've contributed $${alreadyContributed.toFixed(2)} and met today's savings target. You have $${available.toFixed(2)} left for other spending.`
        case 'on-track':
          return `You've contributed $${alreadyContributed.toFixed(2)} so far. You can afford the remaining $${remainingTarget.toFixed(2)} and still have $${(available - remainingTarget).toFixed(2)} left.`
        case 'partial':
          return `You've contributed $${alreadyContributed.toFixed(2)} so far. Consider contributing $${available.toFixed(2)} more today, or adjust spending to meet the full target.`
        case 'no-budget':
          return `You've contributed $${alreadyContributed.toFixed(2)} so far. No budget remaining for additional savings today.`
        default:
          return ''
      }
    }
  }
}